import { LanguageDropdown, TextFieldPhone, TextInput } from '@yembo/yemblocks-core';
import { isValidEmail } from '@yembo/yemblocks-core/dist/_util';
import { Dispatch, useEffect, useState } from 'react';
import { noEmptySpace } from 'src/react/_util/validation';
import { ContactJobState, CreateContactFormData, DefaultInsured } from 'src/react/Contact';
import { canPhoneReceiveSms } from 'src/react/Contact/_tools/canPhoneReceiveSms';
import { useMessagingCompanyAvailableLocales } from '../../../../../queries/Messaging/messaging';
import { languages } from '../../../../_shared/Elements/Dropdowns/LanguageDropdown/_util/languages';

type Props = {
  dispatchInsured: (value: Partial<DefaultInsured>) => void;
  dispatchFormData: Dispatch<Partial<CreateContactFormData>>;
  dispatchJobState: Dispatch<Partial<ContactJobState>>;
  formData: CreateContactFormData;
  job: ContactJobState;
  setIsPhoneValid: (value: boolean) => void;
  isNoReviewCompany: boolean;
  setHandleAnimationEnd?: React.Dispatch<React.SetStateAction<(() => void) | undefined>>;
};

export const ContactSection = ({
  dispatchInsured,
  formData,
  job,
  dispatchFormData,
  dispatchJobState,
  setIsPhoneValid,
  isNoReviewCompany,
  setHandleAnimationEnd,
}: Props) => {
  const [nameInputRef, setNameInputRef] = useState<HTMLInputElement | null>(null);

  const { insured, company, validation } = formData;

  const { data: companyAvailableLocales } = useMessagingCompanyAvailableLocales(company.key);
  const availableLocaleOptions = languages.filter((language) => companyAvailableLocales?.includes(language.locale));

  const { givenName, familyName, email, phone } = insured;
  const { defaultCountry, key: companyKey } = company;

  const updatePhone = (phone: string, isPhoneValid: boolean) => {
    dispatchInsured({ phone });
    setIsPhoneValid(isPhoneValid);
  };

  const hasNoPhoneNoEmail = !email && !phone;
  const hasEmailNoPhone = email && !phone;
  const hasPhoneNoEmail = phone && !email;
  const hasPhoneAndEmail = Boolean(phone && email);

  useEffect(() => {
    if (!nameInputRef || !setHandleAnimationEnd) return;

    // set the callback function to be executed AFTER Drawer animation ends
    setHandleAnimationEnd(() => () => nameInputRef.focus());
  }, [nameInputRef, setHandleAnimationEnd]);

  return (
    <div className='section contact-section'>
      <p className='bold header'>Contact</p>
      <div className='column'>
        <div className='inline'>
          <TextInput
            label='First Name'
            name='givenName'
            value={givenName ?? ''}
            required
            pattern={noEmptySpace}
            onChange={(event) => dispatchInsured({ givenName: event.currentTarget.value })}
            inputRef={setNameInputRef}
          />
          <TextInput
            label='Last Name'
            name='familyName'
            value={familyName ?? ''}
            required
            pattern={noEmptySpace}
            onChange={(event) => dispatchInsured({ familyName: event.currentTarget.value })}
          />
        </div>
        <div className='contact-phone-wrap'>
          {
            // Can't use the isPhoneRequired prop from TextFieldPhone because we need to allow phone field to be empty
            // RMC companies need either phone or email required
            isNoReviewCompany && (hasPhoneNoEmail || hasNoPhoneNoEmail || hasPhoneAndEmail) && (
              <span className='tiny-text bold contact-phone-required'>*</span>
            )
          }
          <TextFieldPhone
            onChangeCallback={updatePhone}
            onSubmitCallback={updatePhone}
            defaultCountry={defaultCountry}
            value={phone ?? ''}
            customErrorMessage="This phone is invalid or can't receive SMS"
            onPhoneCheck={async (phone) => await canPhoneReceiveSms({ phone, companyKey })}
          />
        </div>
        <TextInput
          label='Email'
          name='email'
          required={isNoReviewCompany && (hasEmailNoPhone || hasNoPhoneNoEmail || hasPhoneAndEmail)}
          value={email ?? ''}
          inputMode='email'
          onChange={({ currentTarget: { value } }) => {
            const email = value.replaceAll(' ', '');
            dispatchFormData({
              insured: { ...insured, email },
              validation: {
                ...validation,
                isEmailValid: !email || isValidEmail(email),
              },
            });
          }}
          checkValidity={() => (validation.isEmailValid ? undefined : 'Invalid email')}
        />
        {availableLocaleOptions.length > 1 && (
          <LanguageDropdown
            label='Language'
            selectedLocale={job.locale}
            onSelect={(locale) => dispatchJobState({ locale })}
            languages={availableLocaleOptions}
          />
        )}
      </div>
    </div>
  );
};
